import React from "react";

import { deleteFromStorage, useLocalStorage } from "@rehooks/local-storage";
import PropTypes from "prop-types";

import { useAuthentication } from "libs/authentication";

import PreorderContext from "./PreorderContext";

/**
 * Provide context about preorder (cart and wallet) of the App.
 */
const PreorderContextProvider = ({ organizationId, widgetUUID, children }) => {
  const { isAuthenticated } = useAuthentication();
  const PREORDER_INFO_KEY = `preorder-${organizationId}-${widgetUUID}`;

  const [preorderInfoLocalStorage, setPreorderInfoLocalStorage] = useLocalStorage(
    PREORDER_INFO_KEY,
    { walletId: null, cartUUID: null },
  );

  const cartUUID = React.useMemo(
    () => preorderInfoLocalStorage?.cartUUID,
    [preorderInfoLocalStorage],
  );

  const walletId = React.useMemo(
    () => preorderInfoLocalStorage?.walletId,
    [preorderInfoLocalStorage],
  );

  const deletePreorderInfoFromLocalStorage = React.useCallback(
    () => {
      try {
        deleteFromStorage(PREORDER_INFO_KEY);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Impossible to use localStorage to delete");
      }
    },
    [PREORDER_INFO_KEY],
  );

  const setCartUUID = React.useCallback(
    (newCartUUID) => {
      setPreorderInfoLocalStorage({ walletId, cartUUID: newCartUUID });
    },
    [walletId, setPreorderInfoLocalStorage],
  );

  const setWalletId = React.useCallback(
    (newWalletId) => {
      setPreorderInfoLocalStorage({ cartUUID, walletId: newWalletId });
    },
    [cartUUID, setPreorderInfoLocalStorage],
  );

  React.useEffect(
    () => {
      if (!isAuthenticated) {
        setWalletId(null);
      }
    },
    [isAuthenticated, setWalletId],
  );

  const value = {
    cartUUID,
    walletId,
    setCartUUID,
    setWalletId,
    deletePreorderInfoFromLocalStorage,
  };

  return (
    <PreorderContext.Provider value={value}>
      {children}
    </PreorderContext.Provider>
  );
};

PreorderContextProvider.propTypes = {
  /**
   * The ID of the organization to scope the authentication.
   */
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /**
   * The UUID of the widget to scope the authentication.
   */
  widgetUUID: PropTypes.string,
  /**
   * The wrapped components that will have access to the context.
   */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.object]).isRequired,
};

PreorderContextProvider.defaultProps = {
  widgetUUID: null,
};

export default PreorderContextProvider;
